import { useEffect, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Slider,
    IconButton,
    Box,
    useTheme,
} from '@mui/material';
import download from 'downloadjs';
import { toPng } from 'html-to-image';

import { cleanTitle } from '@/helpers/users-helper';

const ExportTierlistToPNG = ({ targetId, defaultTitle, width, height, showTitle = true, icon = false }) => {
    const [open, setOpen] = useState(false);
    const [imageTitle, setImageTitle] = useState(defaultTitle);
    const [pixelRatio, setPixelRatio] = useState(3); // Valor predeterminado para la calidad
    const theme = useTheme();

    useEffect(() => {
        setImageTitle(defaultTitle);
    }, [defaultTitle]);

    const handleExport = async () => {
        const node = document.getElementById(targetId);
        if (!node) {
            return;
        }

        // Crear un div temporal para combinar el título y el contenido del targetId
        const exportContainer = document.createElement('div');

        // Clonar el contenido de targetId
        const contentClone = node.cloneNode(true);

        // Crear el título con la fuente Roboto Condensed
        if (showTitle) {
            const titleElement = document.createElement('h2');
            titleElement.textContent = `Tierlist: ${imageTitle}`;
            titleElement.style.fontSize = '20px';
            titleElement.style.paddingLeft = '8px';
            titleElement.style.paddingTop = '8px';
            titleElement.style.paddingBottom = '8px';
            titleElement.style.fontFamily = "'Roboto Condensed', sans-serif";
            titleElement.style.color = theme.palette.text.primary;
            titleElement.style.backgroundColor = theme.palette.background.default;
            titleElement.style.width = '100%';

            exportContainer.appendChild(titleElement);
        }

        // Añadir el título y el contenido clonado al contenedor
        exportContainer.appendChild(contentClone);

        // Aplicar estilos al exportContainer respetando el tema actual
        exportContainer.style.fontFamily = "'Roboto Condensed', sans-serif";
        exportContainer.style.backgroundColor = theme.palette.background.default;
        exportContainer.style.color = theme.palette.text.primary;

        // Aplicar width y height si están definidos
        if (width) {
            exportContainer.style.width = `${width}px`;
        }
        if (height) {
            exportContainer.style.height = `${height}px`;
        }

        document.body.appendChild(exportContainer); // Añadir el contenedor al DOM temporalmente para exportarlo

        try {
            const dataUrl = await toPng(exportContainer, {
                cacheBust: true,
                skipFonts: false, // Permitir la inclusión de fuentes
                pixelRatio, // Utilizar el valor de pixelRatio seleccionado
                width: width || undefined, // Solo incluir si está definido
                height: height || undefined, // Solo incluir si está definido
            });

            // Limpiar el título para que sea un nombre de archivo válido
            const cleanedTitle = cleanTitle(imageTitle);
            download(dataUrl, `${cleanedTitle}.png`);
        } catch (err) {
            console.error('Error exporting image:', err);
        }

        document.body.removeChild(exportContainer); // Remover el contenedor temporal del DOM
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = (confirm) => {
        if (confirm) {
            handleExport(); // Solo exportar si se confirma
        }
        setOpen(false);
    };

    const handlePixelRatioChange = (_event, value) => {
        setPixelRatio(value);
    };

    const getTextExport = () => {
        if (icon) {
            return (
                <IconButton
                    color="primary"
                    onClick={handleClickOpen}
                    aria-label="download image"
                    style={{ padding: '8px' }}
                >
                    <DownloadIcon />
                </IconButton>
            );
        } else {
            return (
                <Button
                    color={'secondary'}
                    variant="outlined"
                    onClick={handleClickOpen}
                    style={{ height: '35px', width: '100%' }}
                >
                    Exportar imagen
                </Button>
            );
        }
    };

    return (
        <Box>
            {getTextExport()}
            {/* Modal para escribir el título y seleccionar la calidad */}
            <Dialog open={open} onClose={() => handleClose(false)}>
                <DialogTitle>Exportar imagen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        El título aparecerá dentro de la imagen y será el nombre del archivo descargado.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Título"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={imageTitle}
                        onChange={(e) => setImageTitle(e.target.value)}
                    />
                    <DialogContentText style={{ marginTop: '16px' }}>
                        Selecciona la calidad de la imagen (1-10):
                    </DialogContentText>
                    <Slider
                        value={pixelRatio}
                        onChange={handlePixelRatioChange}
                        min={1}
                        max={10}
                        step={1}
                        valueLabelDisplay="auto"
                        marks={[
                            { value: 1, label: '1' },
                            { value: 10, label: '10' },
                        ]}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleClose(false)}>Cancelar</Button>
                    <Button onClick={() => handleClose(true)}>Confirmar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExportTierlistToPNG;
